import { Link } from '@tanstack/react-router';
import { ExternalLink, Lock, MailX, RotateCw, Send, TriangleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  FAILURE_CAUSE_INFO,
  MESSAGE_ACTION_REFUSAL_TEXT,
  MESSAGE_DELIVERY_STATE_LABELS,
  emailSchema,
  secretLinkAdvice,
  type MessageActionRefusal,
  type MessageDeliveryDetail,
  type MessageDeliveryRow,
} from '@nest-recruit/shared';
import { Input } from '../../../../components/Input';
import { SkeletonLines } from '../../../../components/Skeleton';
import { ServerErrorState } from '../../../../components/system-states/SystemState';
import { ToneChip } from '../../../../components/Tone';
import { errorMessage } from '../../../../lib/api-client';
import { EMPTY, formatDateTime } from '../../../../lib/format';
import { isLinkMissingItsCode } from '../../../../lib/token-links';
import { useToasts } from '../../../../stores/toasts';
import { useDismissMessage, useMessageDetail, useReaddressMessage, useResendMessage } from '../../api/email-delivery-api';
import { ConfirmBeside } from '../ConfirmBeside';
import { PaneFrame, PaneSection, type PaneMode } from '../people/PaneFrame';
import { STATE_TONES, messageLine, plural } from './email-delivery-address';
import styles from './MessagePane.module.css';

/**
 * Email delivery's pane (Option A): the reason in plain words with the server's code beside it, the typo hint, Fix
 * address, Send again and Dismiss (each asked beside its button, none with an Undo: the email goes), what happened
 * try by try, and the details. Every refusal is the server's, explained before anyone clicks.
 */
export function MessagePane({
  id,
  row,
  mode,
  canEdit,
  onOpen,
  onClose,
}: {
  readonly id: string;
  /** The list's copy, shown at once while the full detail loads. */
  readonly row: MessageDeliveryRow | undefined;
  readonly mode: PaneMode;
  readonly canEdit: boolean;
  readonly onOpen: (id: string) => void;
  readonly onClose: () => void;
}): ReactNode {
  const detail = useMessageDetail(id, true);
  const shown: MessageDeliveryRow | undefined = detail.data ?? row;

  if (!shown) {
    return (
      <PaneFrame mode={mode} label="Email" title={detail.isError ? 'Email' : 'Loading…'} onClose={onClose}>
        {detail.isError ? <ServerErrorState requestId={detail.error.requestId} onRetry={() => void detail.refetch()} /> : <PaneSection label="Loading"><SkeletonLines count={6} /></PaneSection>}
      </PaneFrame>
    );
  }

  const tries = shown.attemptCount;
  return (
    <PaneFrame
      mode={mode}
      label={`Email: ${shown.subject}`}
      title={shown.subject}
      sub={`To ${shown.recipientName ? `${shown.recipientName} — ` : ''}${shown.toAddress}`}
      leading={<ToneChip tone={STATE_TONES[shown.state]}>{MESSAGE_DELIVERY_STATE_LABELS[shown.state]}</ToneChip>}
      onClose={onClose}
    >
      <PaneSection label="What went wrong">
        {tries > 0 && <span className={styles.meta}>{plural(tries, 'try', 'tries')}</span>}
        <Reason row={shown} detail={detail.data} />
      </PaneSection>

      {detail.data && <Actions detail={detail.data} canEdit={canEdit} onOpen={onOpen} />}

      <PaneSection title="What happened">{detail.data ? <History detail={detail.data} onOpen={onOpen} /> : <SkeletonLines count={3} />}</PaneSection>

      <PaneSection title="Details">
        <dl className={styles.details}>
          <dt>Message</dt>
          <dd>{messageLine(shown)}</dd>
          <dt>Created</dt>
          <dd className={styles.number}>{formatDateTime(shown.createdAt)}</dd>
          <dt>Sent</dt>
          <dd className={styles.number}>{shown.sentAt ? formatDateTime(shown.sentAt) : EMPTY}</dd>
          <dt>Link in email</dt>
          <dd>{shown.deepLinkUrl ? <DeepLink url={shown.deepLinkUrl} /> : shown.holdsSecret ? <span className={styles.hiddenLink}><Lock size={12} aria-hidden /> {HIDDEN_LINK_NOTE}</span> : EMPTY}</dd>
          <dt>About</dt>
          <dd>
            {shown.aboutName && shown.candidateId ? (
              <Link to="/candidates/$candidateId" params={{ candidateId: shown.candidateId }} className={styles.link}>
                {shown.aboutName}
                {shown.aboutJob ? ` — ${shown.aboutJob}` : ''}
              </Link>
            ) : (
              EMPTY
            )}
          </dd>
        </dl>
      </PaneSection>
    </PaneFrame>
  );
}

const HIDDEN_LINK_NOTE = "Link hidden for security — only the recipient's email has the working link.";

/** The plain reason, the server's own words beside it, and the typo hint. */
function Reason({ row, detail }: { readonly row: MessageDeliveryRow; readonly detail: MessageDeliveryDetail | undefined }): ReactNode {
  if (row.state === 'retrying') {
    return (
      <p className={styles.note}>
        <RotateCw size={16} aria-hidden />
        <span>
          <b>Still trying.</b> {row.cause ? `${FAILURE_CAUSE_INFO[row.cause].label}. ` : ''}Another try is coming, so it can't be sent again yet.
          {row.failureReason && <span className={styles.mono}> {row.failureReason}</span>}
        </span>
      </p>
    );
  }
  if (row.cause === null || (row.state !== 'attention' && row.state !== 'dismissed' && row.state !== 'replaced')) return null;
  const info = FAILURE_CAUSE_INFO[row.cause];
  const suggested = detail?.suggestedAddress ?? null;
  return (
    <div className={styles.alarm} role="note">
      <TriangleAlert size={16} aria-hidden />
      <span>
        <b>{info.label}.</b> {row.failureReason ? <>The server said <span className={styles.mono}>{row.failureReason}</span>. </> : null}
        {suggested ? (
          <>
            <b>{domainOf(row.toAddress)}</b> is probably <b>{domainOf(suggested)}</b>.
          </>
        ) : (
          info.advice
        )}
      </span>
    </div>
  );
}

function domainOf(address: string): string {
  return address.slice(address.lastIndexOf('@') + 1);
}

function refusalText(refusal: MessageActionRefusal, detail: MessageDeliveryDetail): ReactNode {
  if (refusal !== 'holds_secret') return MESSAGE_ACTION_REFUSAL_TEXT[refusal];
  const advice = secretLinkAdvice(detail.templateKey);
  // Owner Q4: point to People → Resend invite (or Reset password) instead.
  const link =
    detail.templateKey === 'user.invited' ? (
      <Link to="/admin/users" search={{ status: 'invited' }} className={styles.link}>
        {advice.text}
      </Link>
    ) : detail.templateKey === 'auth.password_reset' && detail.userId ? (
      <Link to="/admin/users/$userId" params={{ userId: detail.userId }} className={styles.link}>
        {advice.text}
      </Link>
    ) : (
      advice.text
    );
  return (
    <>
      {MESSAGE_ACTION_REFUSAL_TEXT.holds_secret} {link}
    </>
  );
}

/** Fix address, Send again and Dismiss, each asked beside its button (owner Q4). Hidden from a read-only Admin. */
function Actions({ detail, canEdit, onOpen }: { readonly detail: MessageDeliveryDetail; readonly canEdit: boolean; readonly onOpen: (id: string) => void }): ReactNode {
  const showToast = useToasts((state) => state.show);
  const resend = useResendMessage();
  const readdress = useReaddressMessage();
  const dismiss = useDismissMessage();
  const [typed, setTyped] = useState<string | null>(null);
  const address = typed ?? detail.suggestedAddress ?? detail.toAddress;
  const parsed = emailSchema.safeParse(address);
  const isSame = parsed.success && parsed.data === detail.toAddress.trim().toLowerCase();
  const failed = (title: string) => (error: unknown) => showToast({ tone: 'danger', message: title, detail: errorMessage(error) });

  const isFailure = detail.state === 'attention' || detail.state === 'dismissed';
  if (!isFailure) return null;
  if (!canEdit) {
    return (
      <PaneSection label="Read only">
        <p className={styles.quiet}>You can look at this email, but not send it again or dismiss it.</p>
      </PaneSection>
    );
  }

  return (
    <PaneSection title="Fix the address and send again">
      {detail.readdressRefusal === null ? (
        <>
          <Input
            aria-label="Corrected address"
            type="email"
            value={address}
            isInvalid={!parsed.success}
            onChange={(event) => setTyped(event.target.value)}
          />
          {!parsed.success && <span className={styles.error}>Enter an email address in the form name@example.com.</span>}
          <p className={styles.quiet}>A fresh copy goes to this address. {detail.aboutName ?? 'Their'} record is not changed, and this email keeps its history.</p>
        </>
      ) : (
        <p className={styles.refusal}>{refusalText(detail.readdressRefusal, detail)}</p>
      )}
      <div className={styles.buttons}>
        {detail.readdressRefusal === null && (
          <ConfirmBeside
            label="Send to this address"
            icon={Send}
            variant="solid"
            disabled={!parsed.success || isSame}
            isPending={readdress.isPending}
            title="Send a fresh copy?"
            body={
              <>
                “{detail.subject}” goes to <b>{parsed.success ? parsed.data : address}</b>. It can't be taken back once it has gone.
              </>
            }
            confirmLabel="Send"
            onConfirm={() =>
              parsed.success &&
              readdress.mutate(
                { id: detail.id, toAddress: parsed.data },
                {
                  onSuccess: (result) => {
                    showToast({ tone: 'success', message: `Sent to ${result.copy.toAddress}` });
                    onOpen(result.copy.id);
                  },
                  onError: failed('The copy was not sent'),
                },
              )
            }
          />
        )}
        {detail.resendRefusal === null && (
          <ConfirmBeside
            label="Send again to the old address"
            icon={RotateCw}
            isPending={resend.isPending}
            title="Send it again?"
            body={
              <>
                “{detail.subject}” goes to <b>{detail.toAddress}</b> again, as it was. It can't be taken back once it has gone.
              </>
            }
            confirmLabel="Send again"
            onConfirm={() => resend.mutate(detail.id, { onSuccess: () => showToast({ tone: 'success', message: 'Sent again' }), onError: failed('It was not sent again') })}
          />
        )}
        {detail.dismissRefusal === null && (
          <ConfirmBeside
            label="Dismiss"
            icon={MailX}
            variant="ghost"
            isPending={dismiss.isPending}
            title="Dismiss this failure?"
            body="Nobody will send it again. It leaves Needs attention and the red count; its history stays here."
            confirmLabel="Dismiss"
            onConfirm={() => dismiss.mutate(detail.id, { onSuccess: () => showToast({ tone: 'success', message: 'Dismissed' }), onError: failed('It was not dismissed') })}
          />
        )}
      </div>
      {detail.resendRefusal !== null && detail.readdressRefusal === null && <p className={styles.quiet}>{refusalText(detail.resendRefusal, detail)}</p>}
    </PaneSection>
  );
}

interface Step {
  readonly at: string;
  readonly text: ReactNode;
  readonly tone: 'bad' | 'good' | 'plain';
}

/** Created, each try, and what people did, in order. Tries before 2 November 2026 only have a count. */
function History({ detail, onOpen }: { readonly detail: MessageDeliveryDetail; readonly onOpen: (id: string) => void }): ReactNode {
  const steps: Step[] = [{ at: detail.createdAt, text: 'Queued', tone: 'plain' }];
  detail.attempts.forEach((attempt, index) => {
    steps.push({ at: attempt.at, text: attempt.ok ? 'Sent' : `Try ${index + 1} failed${attempt.reason ? `: ${attempt.reason}` : ''}`, tone: attempt.ok ? 'good' : 'bad' });
  });
  for (const action of detail.actions) {
    const by = action.byName ? ` by ${action.byName}` : '';
    const text =
      action.kind === 'failed' ? 'Stopped trying' : action.kind === 'sent_again' ? `Sent again${by}` : action.kind === 'dismissed' ? `Dismissed${by}` : `Fixed address: a copy went to ${action.toAddress ?? EMPTY}${by}`;
    steps.push({ at: action.at, text, tone: action.kind === 'failed' ? 'bad' : 'plain' });
  }
  steps.sort((a, b) => a.at.localeCompare(b.at));
  const unrecorded = detail.attemptCount - detail.attempts.length;

  return (
    <>
      <ol className={styles.timeline}>
        {steps.map((step, index) => (
          <li key={`${step.at}-${index}`} className={styles.step} data-tone={step.tone}>
            <span className={styles.stepText}>{step.text}</span>
            <span className={styles.number}>{formatDateTime(step.at)}</span>
          </li>
        ))}
      </ol>
      {unrecorded > 0 && <p className={styles.quiet}>{plural(unrecorded, 'earlier try', 'earlier tries')}, from before each try's time was kept.</p>}
      {detail.replacedBy && (
        <p className={styles.quiet}>
          A corrected copy went to {detail.replacedBy.toAddress}.{' '}
          <button type="button" className={styles.textButton} onClick={() => onOpen(detail.replacedBy?.id ?? detail.id)}>
            Open it
          </button>
        </p>
      )}
      {detail.copyOf && (
        <p className={styles.quiet}>
          This is a corrected copy of the email to {detail.copyOf.toAddress}.{' '}
          <button type="button" className={styles.textButton} onClick={() => onOpen(detail.copyOf?.id ?? detail.id)}>
            Open the original
          </button>
        </p>
      )}
    </>
  );
}

/** A reset, invite or candidate link is kept without its code: shown, not offered as a link (D17). */
function DeepLink({ url }: { readonly url: string }): ReactNode {
  if (isLinkMissingItsCode(url)) {
    return (
      <span className={styles.hiddenLink}>
        <span className={styles.mono}>
          <Lock size={12} aria-hidden /> {url}
        </span>
        <span>{HIDDEN_LINK_NOTE}</span>
      </span>
    );
  }
  return (
    <a className={styles.link} href={url} target="_blank" rel="noreferrer">
      <ExternalLink size={12} aria-hidden /> {url}
    </a>
  );
}
